import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InstallAppButton } from "@/components/InstallAppButton";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { useAuth, homeFor } from "@/context/AuthContext";
import { errMsg, api } from "@/lib/api";
import { ForgotPasswordDialog } from "@/components/ForgotPasswordDialog";

const HERO = "https://images.hostinger.com/6d8ed5ec-3b0b-4c0e-8b67-8a96f81ff4a4.png";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [brand, setBrand] = useState({});
  const [forgot, setForgot] = useState(false);
  useEffect(() => { api.get("/brand").then((r) => setBrand(r.data)).catch(() => {}); }, []);

  if (user) return <Navigate to={homeFor(user.role)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(email, password);
      nav(homeFor(u.role), { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#FBF9F4]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Fresh dosa batter" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F382C] via-[#0F382C]/60 to-transparent" />
        <div className="absolute bottom-0 p-12 text-white max-w-xl bf-rise">
          <div className="text-xs uppercase tracking-[0.3em] text-[#F2B880]">From market to doorstep</div>
          <h1 className="font-display text-5xl font-extrabold leading-[1.05] mt-4">Grind. Deliver. Get paid. On autopilot.</h1>
          <p className="mt-4 text-emerald-50/85">WhatsApp order confirmations, production planning, photo-proof delivery and weekly credit collection in one place.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm bf-rise bf-d1" data-testid="login-form">
          <div className="flex items-center gap-3">
            {brand.logo_url && <img src={brand.logo_url} alt="logo" className="h-12 w-12 object-contain" data-testid="login-logo" />}
            <div className="font-display text-3xl font-extrabold text-[#0F382C]" data-testid="login-brand">{brand.business_name || <>Batter<span className="text-[#D96B43]">Flow</span></>}</div>
          </div>
          <p className="text-muted-foreground mt-2 mb-10">{brand.tagline ? `${brand.tagline}. ` : ""}Sign in to run today's batch.</p>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 bg-white" data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button type="button" onClick={() => setForgot(true)} className="text-xs font-medium text-[#D96B43] hover:underline" data-testid="forgot-password-link">Forgot password?</button>
              </div>
              <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 bg-white" data-testid="login-password-input" />
            </div>
            {error && <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={busy} className="h-12 w-full rounded-full bg-[#0F382C] hover:bg-[#134E3A] text-base" data-testid="login-submit-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </div>
          <InstallAppButton variant="outline" label="Install Nex Fresh app" className="mt-4 h-11 w-full rounded-full border-[#0F382C]/30 text-[#0F382C]" testId="login-install-app-btn" />
        </form>
        {forgot && <ForgotPasswordDialog open={forgot} onOpenChange={setForgot} initialEmail={email} />}
      </div>
    </div>
  );
}
